import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { WarrantySystemView } from "@redotech/redo-model/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  OrderManagementSuiteStatusesContext,
  WARRANTIES_ICON,
  WARRANTIES_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { WarrantyViewOrderingContext } from "../../../../services/warranties/warranty-view-ordering-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const WarrantiesItem = memo(function WarrantiesItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { warrantiesEnabled, warrantyRegistrationEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const hasPermissionToViewItem = useHasPermission(
    OrderManagementPermissions.MANAGE_WARRANTY,
  );

  const warrantyViewOrderingContext = useRequiredContext(
    WarrantyViewOrderingContext,
  );

  const allViews = useMemo(() => {
    return [...warrantyViewOrderingContext.views].filter(
      (view) => view.name !== WarrantySystemView.CREATE,
    );
  }, [warrantyViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...warrantyViewOrderingContext.folders];
  }, [warrantyViewOrderingContext]);

  const notificationCounts = warrantyViewOrderingContext.notificationCounts;

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"warranties">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: routes.warrantiesHref,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    routes.warrantiesHref,
    notificationCounts,
    folderNotificationCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    warrantyViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"warranties">(
      flattenedItems,
      allViews,
      allFolders,
    );
    warrantyViewOrderingContext.updateOrdering(views, folders);
  });

  const registrationLink = routes.registrationsHref;

  const warrantiesMatch = useMatch(
    `${routes.getWarrantiesHref({ enabled: true })}/*`,
  );
  const registrationsMatch = useMatch(`${registrationLink}/*`);
  const anyChildSelected = warrantiesMatch || registrationsMatch;

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getWarrantiesHref({ enabled: warrantiesEnabled }),
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={WARRANTIES_ICON_FILLED}
      friendlyName="Warranties"
      id="warranties"
      notificationCount={totalNotificationCount}
      UnfilledIcon={WARRANTIES_ICON}
    >
      {warrantiesEnabled && (
        <>
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getWarrantiesSubRoute("all-warranties"),
            }}
            friendlyName="All warranties"
            id="all-warranties"
          />
          <SortableTree
            items={sortableItems}
            onRequestEdit={handleRequestEdit}
            setItems={handleUpdateOrdering}
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: routes.getWarrantiesSubRoute("create"),
            }}
            friendlyName="+ Add new view"
            id="orders-warranties-create"
          />
          <RedoSecondaryNavItem
            action={{
              type: "button",
              onClick: () => {
                warrantyViewOrderingContext.openCreateFolderModal();
              },
            }}
            friendlyName="+ Add new folder"
            id="orders-warranties-create-folder"
          />
        </>
      )}
      {warrantyRegistrationEnabled && (
        <RedoSecondaryNavItem
          action={{ type: "link", href: registrationLink }}
          friendlyName="Registrations"
          id="registrations"
        />
      )}
    </RedoSecondaryNavItem>
  );
});
